export type AnalyticsConsentChoice = 'granted' | 'denied'

export const ANALYTICS_CONSENT_KEY = 'ah_analytics_consent_v1'
export const ANALYTICS_CONSENT_GRANTED_EVENT = 'ah-analytics:consent-granted'
export const OPEN_ANALYTICS_CONSENT_EVENT = 'ah-analytics:open-consent'

const META_PIXEL_ID = '476181829980801'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  loaded: boolean
  version: string
}

type MarketingWindow = Window & { fbq?: Fbq }

export function getAnalyticsConsentChoice(): AnalyticsConsentChoice | null {
  if (typeof window === 'undefined') return null
  try {
    const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY)
    return value === 'granted' || value === 'denied' ? value : null
  } catch {
    return null
  }
}

// Hosts that must never load GA or the Meta pixel: local dev and the prerender
// server, plus Firebase's default *.web.app / *.firebaseapp.com domains (preview
// channels and the raw project URLs), which are not the real site.
const NON_TRACKING_HOSTNAME =
  /^(localhost|127\.0\.0\.1|\[::1\])$|\.web\.app$|\.firebaseapp\.com$/

export function isTrackingEnvironment(): boolean {
  return (
    import.meta.env.PROD &&
    typeof window !== 'undefined' &&
    !NON_TRACKING_HOSTNAME.test(window.location.hostname)
  )
}

export function isMarketingTrackingAllowed(): boolean {
  return isTrackingEnvironment() && getAnalyticsConsentChoice() === 'granted'
}

function ensureMetaPixel(): Fbq | null {
  if (!isMarketingTrackingAllowed()) return null
  const marketingWindow = window as MarketingWindow
  if (marketingWindow.fbq) return marketingWindow.fbq

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue.push(args)
  }) as Fbq
  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  marketingWindow.fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  fbq('init', META_PIXEL_ID)
  return fbq
}

function metaEventName(eventName: string): string {
  return eventName
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')
}

export function logMetaPageView(): void {
  ensureMetaPixel()?.('track', 'PageView')
}

export function logMetaEvent(name: string, params: Record<string, unknown> = {}): void {
  const fbq = ensureMetaPixel()
  if (!fbq) return

  const standardEvent = {
    begin_checkout: 'InitiateCheckout',
    generate_lead: 'Lead',
    purchase: 'Purchase',
    sign_up: 'CompleteRegistration',
  }[name]

  if (standardEvent) fbq('track', standardEvent, params)
  else fbq('trackCustom', metaEventName(name), params)
}

export function clearMarketingCookies(): void {
  const names = document.cookie
    .split(';')
    .map((item) => item.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name))
  const hostParts = window.location.hostname.split('.')
  const parentDomain = hostParts.length > 1 ? `.${hostParts.slice(-2).join('.')}` : ''

  for (const name of names) {
    if (!name.startsWith('_ga') && !name.startsWith('_fb') && name !== '_fbc' && name !== '_fbp') continue
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
    if (parentDomain) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${parentDomain}; SameSite=Lax`
    }
  }
}

export function openAnalyticsConsent(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OPEN_ANALYTICS_CONSENT_EVENT))
  }
}
